import { AirTimeRegistrationRequestModel } from "features/airTimeRegistration/data/model/AirTimeRegistrationRequestModel";
import { ServiceEnum } from "utils/enums/ServiceEnum";

export interface SelectOption {
  label?: string;
  value?: string;
}

export interface SearchFormValues {
  created?: Array<Record<string, unknown>>;
  status?: number | string;
  code?: string;
  transRef?: string;
  supplierRefCode?: string;
  carrierCodes?: string[];
  phoneNumber?: string;
  partnerCode?: string;
  codeAirTime?: string;
  userRef?: string[];
  storeCode?: string;
}

export interface SearchContext {
  staffOptions: SelectOption[];
  userOptions: SelectOption[];
  selectedProviderCode?: string;
  selectedPartnerType: number;
  isStore: boolean;
  storeCode?: string;
  limit: number;
}

export class AirTimeRegistrationQuery {
  static resolveUserRef(
    rawUserRef: string[] | undefined,
    staffOptions: SelectOption[],
    userOptions: SelectOption[],
    selectedPartnerType: number,
    isStore: boolean,
  ): string[] | undefined {
    if (!rawUserRef || rawUserRef.length === 0) return undefined;

    if (selectedPartnerType === 1 && isStore) {
      return rawUserRef.filter((ele) =>
        staffOptions?.some((child) => child?.value === ele),
      );
    }
    if (selectedPartnerType === 2 || (selectedPartnerType === 1 && !isStore)) {
      return rawUserRef.filter((ele) =>
        userOptions?.some((child) => child?.value === ele),
      );
    }
    return undefined;
  }

  static sanitize<T extends Record<string, unknown>>(obj: T): T {
    const result = { ...obj };
    Object.keys(result).forEach((key) => {
      const v = result[key];
      if (
        v === undefined ||
        v === null ||
        (typeof v === "string" && v.trim() === "")
      ) {
        delete result[key];
      }
    });
    return result as T;
  }

  static buildSearchQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): AirTimeRegistrationRequestModel {
    const finalUserRef = this.resolveUserRef(
      formValues.userRef,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new AirTimeRegistrationRequestModel({
      fromDate: dateFrom,
      toDate: dateTo,
      status: formValues.status as number | undefined,
      code: formValues.code,
      transRef: formValues.transRef,
      supplierRefCode: formValues.supplierRefCode,
      carrierCodes: formValues.carrierCodes,
      phoneNumber: formValues.phoneNumber,
      storeCodes: [context.storeCode ?? formValues.storeCode],
      userRef: finalUserRef,
      partnerCodes: [formValues.partnerCode],
      providerCode: context.selectedProviderCode,
      codeAirTime: formValues.codeAirTime,
      page: 0,
      limit: context.limit ?? 10,
      serviceCode: ServiceEnum.AIR_TIME,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as AirTimeRegistrationRequestModel;
  }

  static buildExportQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): AirTimeRegistrationRequestModel {
    const finalUserRef = this.resolveUserRef(
      formValues.userRef,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new AirTimeRegistrationRequestModel({
      fromDate: dateFrom,
      toDate: dateTo,
      status: formValues.status as number | undefined,
      code: formValues.code,
      transRef: formValues.transRef,
      supplierRefCode: formValues.supplierRefCode,
      carrierCodes: formValues.carrierCodes,
      phoneNumber: formValues.phoneNumber,
      storeCodes: [context.storeCode ?? formValues.storeCode],
      userRef: finalUserRef,
      partnerCodes: [formValues.partnerCode],
      providerCode: context.selectedProviderCode,
      codeAirTime: formValues.codeAirTime,
      serviceCode: ServiceEnum.AIR_TIME,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as AirTimeRegistrationRequestModel;
  }

  static buildTableChangeQuery(
    prevParams: AirTimeRegistrationRequestModel,
    formValues: Partial<SearchFormValues>,
    page: number,
    limit: number,
  ): AirTimeRegistrationRequestModel {
    const { created, ...rest } = formValues;
    const status =
      rest.status !== undefined
        ? typeof rest.status === "string"
          ? Number(rest.status)
          : rest.status
        : prevParams.status;
    const raw = new AirTimeRegistrationRequestModel({
      ...prevParams,
      ...rest,
      status: status as number | undefined,
      page,
      limit,
    });
    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as AirTimeRegistrationRequestModel;
  }

  static buildRedoQuery(
    fromDate: string,
    toDate: string,
  ): AirTimeRegistrationRequestModel {
    return new AirTimeRegistrationRequestModel({
      page: 0,
      limit: 10,
      fromDate,
      toDate,
      serviceCode: ServiceEnum.AIR_TIME,
    });
  }
}
